/**
 * Code-file approval (owner 2026-10-27, #1452) in the workspace explorer: a `.py` / `.sql` whose current content no person
 * set shows the drive's Approve badge on its tree row (and its context menu offers Approve). The flow itself is the
 * drive's — `useApproveCode` — handed down once through context, so every tree (the workspace's own and each linked
 * folder's) uses the same confirm, the same version-bound call and the same answers.
 */
import { createContext, useContext, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import type { ApprovableCodeFile } from '@papan/files/ui'

type Approve = (file: ApprovableCodeFile) => Promise<boolean>

const CodeApprovalContext = createContext<Approve | null>(null)

/** Provides the approval flow to every tree row under it. */
export const CodeApprovalProvider = CodeApprovalContext.Provider

/** The approval flow for a context-menu item (null outside a provider). */
export function useCodeApproval(): Approve | null {
  return useContext(CodeApprovalContext)
}

export interface CodeApprovalRow {
  id: string
  name: string
  isFolder: boolean
  codeNeedsApproval?: boolean
  codeCanApprove?: boolean
  /** A folder with a code file needing approval somewhere below it. */
  codeApprovalBelow?: boolean
  contentHash?: string | null
}

/** The row's badge: Approve when the viewer may, else a plain "Needs approval" chip; nothing for any other row. Hidden
 *  once approved here for the content the row listed (a later listing brings the server's answer). */
export function CodeApprovalBadge({ row }: { row: CodeApprovalRow }) {
  const approve = useCodeApproval()
  const { t } = useTranslation('files')
  const [approvedFor, setApprovedFor] = useState<string | null | undefined>(undefined)
  // a folder: a warning that something below it needs approval (open it to find the file's Approve)
  if (row.isFolder) {
    if (!row.codeApprovalBelow) return null
    return (
      <span className="ml-1 shrink-0 inline-flex text-amber-500" data-tooltip={t('driveView.code.folderHasPending')} aria-label={t('driveView.code.folderHasPending')} data-testid="workspace-code-pending-below">
        <AlertTriangle size={11} />
      </span>
    )
  }
  if (!row.codeNeedsApproval || (approvedFor !== undefined && approvedFor === row.contentHash)) return null
  const hint = t('driveView.code.needsApprovalHint')
  if (!row.codeCanApprove || !approve) {
    return (
      <span className="ml-1 shrink-0 inline-flex items-center gap-1 rounded px-1 text-[10px] font-medium bg-amber-500/15 text-amber-500" data-tooltip={hint}>
        <AlertTriangle size={10} />{t('driveView.code.needsApproval')}
      </span>
    )
  }
  return (
    <button
      type="button"
      className="ml-1 shrink-0 inline-flex items-center gap-1 rounded px-1 text-[10px] font-medium bg-amber-500 text-black hover:bg-amber-400"
      data-tooltip={hint}
      data-testid="workspace-code-approve"
      onClick={async (e) => {
        e.stopPropagation()
        if (await approve(row)) setApprovedFor(row.contentHash ?? null)
      }}
    >
      <AlertTriangle size={10} />{t('driveView.code.approve')}
    </button>
  )
}
